import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, RotateCw, Home, Lock, Star, EllipsisVertical, X, Plus, Minus, Maximize } from 'lucide-react';

/**
 * Pixel-perfect Chrome browser UI frame
 * Wraps the actual screenshot to provide full browser context
 */
const ChromeFrame = ({
    currentTab,
    allTabs = [],
    onNavigate,
    onTabSwitch,
    onTabClose,
    onNewTab,
    onAddressSubmit,
    children
}) => {
    const [addressBarValue, setAddressBarValue] = useState(currentTab?.url || '');
    const [isAddressBarFocused, setIsAddressBarFocused] = useState(false);
    const addressInputRef = useRef(null);

    const handleAddressSubmit = (e) => {
        e.preventDefault();
        if (onAddressSubmit) {
            onAddressSubmit(addressBarValue);
        }
        addressInputRef.current?.blur();
    };

    const handleBack = () => {
        if (onNavigate) onNavigate('back');
    };

    const handleForward = () => {
        if (onNavigate) onNavigate('forward');
    };

    const handleReload = () => {
        if (onNavigate) onNavigate('reload');
    };

    const handleHome = () => {
        if (onNavigate) onNavigate('home');
    };

    return (
        <div className="flex flex-col bg-[#202124] rounded-lg overflow-hidden shadow-2xl border border-gray-800 w-full h-full">
            {/* Window Controls */}
            <div className="flex items-center justify-between bg-[#202124] px-3 py-1.5 border-b border-gray-800">
                <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 cursor-pointer" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500 hover:bg-yellow-600 cursor-pointer" />
                        <div className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 cursor-pointer" />
                    </div>
                </div>
                <div className="text-xs text-gray-400 font-medium">Chrome Remote View</div>
                <div className="flex items-center gap-1 opacity-50">
                    <Minus size={14} className="text-gray-400" />
                    <Maximize size={12} className="text-gray-400" />
                    <X size={14} className="text-gray-400" />
                </div>
            </div>

            <div className="flex items-center bg-[#2d2e30] px-2 py-1 gap-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700">
                {Array.isArray(allTabs) && allTabs.map((tab, index) => (
                    tab && (
                        <div
                            key={tab.id || index}
                            onClick={() => onTabSwitch && onTabSwitch(tab.id)}
                            className={`group flex items-center gap-2 px-3 py-1.5 min-w-[150px] max-w-[200px] rounded-t-lg cursor-pointer transition-colors ${tab.id === currentTab?.id
                                ? 'bg-[#202124] text-white'
                                : 'bg-[#35363a] text-gray-400 hover:bg-[#3c3d41]'
                                }`}
                        >
                            <img
                                src={tab.favIconUrl || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>'}
                                alt=""
                                className="w-4 h-4 flex-shrink-0"
                                onError={(e) => e.target.style.display = 'none'}
                            />
                            <span className="flex-1 text-xs truncate font-medium">
                                {tab.title || 'New Tab'}
                            </span>
                            {tab.id === currentTab?.id && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTabClose && onTabClose(tab.id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-700 rounded p-0.5"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    )
                ))}
                <button
                    onClick={() => onNewTab && onNewTab()}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors text-gray-400 hover:text-white"
                >
                    <Plus size={14} />
                </button>
            </div>

            {/* Address Bar & Toolbar */}
            <div className="flex items-center gap-2 bg-[#202124] px-3 py-2 border-b border-gray-800">
                {/* Navigation Buttons */}
                <div className="flex gap-1">
                    <button
                        onClick={handleBack}
                        className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
                        title="Back"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        onClick={handleForward}
                        className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
                        title="Forward"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <button
                        onClick={handleReload}
                        className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
                        title="Reload"
                    >
                        <RotateCw size={16} />
                    </button>
                    <button
                        onClick={handleHome}
                        className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
                        title="Home"
                    >
                        <Home size={16} />
                    </button>
                </div>

                {/* Address Bar */}
                <form onSubmit={handleAddressSubmit} className="flex-1">
                    <div className={`flex items-center gap-2 bg-[#2d2e30] rounded-full px-4 py-1.5 transition-all ${isAddressBarFocused ? 'ring-2 ring-blue-500' : ''
                        }`}>
                        <Lock size={14} className="text-gray-400 flex-shrink-0" />
                        <input
                            ref={addressInputRef}
                            type="text"
                            value={addressBarValue}
                            onChange={(e) => setAddressBarValue(e.target.value)}
                            onFocus={() => {
                                setIsAddressBarFocused(true);
                                setAddressBarValue(currentTab?.url || '');
                                setTimeout(() => addressInputRef.current?.select(), 0);
                            }}
                            onBlur={() => {
                                setIsAddressBarFocused(false);
                                if (!addressBarValue) {
                                    setAddressBarValue(currentTab?.url || '');
                                }
                            }}
                            placeholder="Search Google or type a URL"
                            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-500"
                        />
                        <Star size={16} className="text-gray-400 flex-shrink-0 cursor-pointer hover:text-yellow-400 transition-colors" />
                    </div>
                </form>

                {/* Extension Icons & Menu */}
                <div className="flex items-center gap-1">
                    <button className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12,16.5C9.5,16.5 7.5,14.5 7.5,12S9.5,7.5 12,7.5 16.5,9.5 16.5,12 14.5,16.5 12,16.5M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                        </svg>
                    </button>
                    <button className="p-1.5 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white">
                        <EllipsisVertical size={18} />
                    </button>
                </div>
            </div>

            {/* Content Area (Screenshot) */}
            <div className="flex-1 bg-white overflow-hidden relative">
                {children}
            </div>
        </div>
    );
};

export default ChromeFrame;
